import React from 'react'
import qs from 'qs'
import { useIntl } from 'react-intl'
import {
  IconCaretRight,
  IconFilter,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { Filters, FiltersProps } from 'contrastBase/filters'
import { layoutStyles } from './layout-styles'
import { useLibrariesPageContext } from '../../pages'
import { css } from '@emotion/css'
import { useSplitClient } from 'contrastBase/state'
import {
  Tab,
  TabList,
  TabProvider
} from '@contrast-security-inc/design-system-navigations'
import { useActiveGrid } from '../hooks/useActiveGrid'
import {
  GRID_TYPES,
  LIBRARIES_TABS,
  LibrariesTabType
} from '../../helpers/ui-interfaces'
import {
  LayoutSidebarContent,
  LayoutSidebarContentProps
} from './layout-sidebar-content'
import { SearchInput } from '@contrast-security-inc/design-system-inputs'
import { useSearch } from '../hooks/useSearch'
import { useStaticView } from '../hooks/useStaticView'

type Props = LayoutSidebarContentProps & {
  filterState: FiltersProps
}

export const Layout = ({
  children,
  filterState,
  quickFilters,
  selectedQuickFilterMap
}: React.PropsWithChildren<Props>) => {
  const { formatMessage } = useIntl()
  const { onSearchInputChanged } = useSearch()
  const { canViewStatic } = useStaticView()
  const { applicationID, filterText, gridType } = useLibrariesPageContext()
  const isApplicationsPage = gridType === 'Application'
  const hasSelectedFilters = filterState.hasFilters()
  const useOrgStyling =
    gridType === 'Organization' || gridType === 'OrganizationCLI'
  const { activeGrid, updateActiveGrid } = useActiveGrid({
    gridType: isApplicationsPage ? GRID_TYPES.APPLICATION : GRID_TYPES.CLI,
    applicationID
  })
  const { isSplitTreatmentOn } = useSplitClient()
  const [isOpen, setIsOpen] = React.useState(false)

  const toggleOpen = () => setIsOpen(!isOpen)

  React.useEffect(() => {
    // If there's a quickfilter/filter set on initial load, then open the filters sidepanel
    const params = qs.parse(window.location.href?.split('?')?.[1] || '')
    if (params['quickFilter'] || hasSelectedFilters) {
      setIsOpen(true)
    }
  }, [hasSelectedFilters])

  return (
    <div className={layoutStyles.containerCss(isOpen, isApplicationsPage)}>
      {isOpen ? (
        <div
          className={layoutStyles.leftCss(isOpen)}
          data-testid="layout-filter-panel"
        >
          <LayoutSidebarContent
            quickFilters={quickFilters}
            selectedQuickFilterMap={selectedQuickFilterMap}
          />
          <Filters {...filterState} />
        </div>
      ) : null}
      <div>
        <div className={layoutStyles.headerContainerCss}>
          {useOrgStyling && (
            <Text
              className={layoutStyles.headerTitleCss}
              data-e2e="grid-header-name"
              font="titleH1"
            >
              {formatMessage({ id: 'title_libraries' })}
            </Text>
          )}
          <SearchInput
            className={layoutStyles.searchInputCss}
            onChange={(evt) => onSearchInputChanged(evt.target.value)}
            testId="library-grid-search-input"
            value={filterText || ''}
          />
          {gridType !== 'Server' && (
            <div className={layoutStyles.tabsContainerCss}>
              {isSplitTreatmentOn('PROD-2340_SIR_Projects_Tab') &&
              canViewStatic &&
              useOrgStyling ? (
                <TabProvider value={activeGrid as LibrariesTabType}>
                  <TabList
                    testId="grid-view-selector"
                    onChange={(_event, value) =>
                      updateActiveGrid(value as LibrariesTabType)
                    }
                    className={css`
                      span {
                        background-color: ${theme.color.neutral90};
                      }

                      button:hover:not([data-selected='true'], :disabled) {
                        color: ${theme.color.neutral80};
                      }
                    `}
                  >
                    <Tab
                      label={formatMessage({
                        id: 'title_libraries_static'
                      })}
                      value={LIBRARIES_TABS.STATIC}
                      testId="CLI-grid-selector"
                    />
                    <Tab
                      label={formatMessage({
                        id: 'title_libraries_runtime'
                      })}
                      value={LIBRARIES_TABS.RUNTIME}
                      testId="assess-grid-selector"
                    />
                  </TabList>
                </TabProvider>
              ) : null}
            </div>
          )}
        </div>
        <hr className={layoutStyles.separatorCss} />
        <div className={layoutStyles.filterLinkCss}>
          <Link
            className={layoutStyles.linkCss}
            onClick={toggleOpen}
            testId="layout-filter-panel-button"
          >
            <IconCaretRight
              className={layoutStyles.iconCss(isOpen)}
              size="md"
              color="turquoise90"
            />
            <Text font="bodyStrong">
              {formatMessage({
                id: `action_${isOpen ? 'close' : 'open'}_filters`
              })}
            </Text>
            <IconFilter
              size="md"
              color={hasSelectedFilters ? 'turquoise90' : 'neutral70'}
            />
          </Link>
          {hasSelectedFilters ? (
            <Text color="turquoise90" font="metadata">
              {formatMessage({ id: 'title_filters_applied' })}
            </Text>
          ) : null}
        </div>
        <div className={layoutStyles.childrenCss(isApplicationsPage)}>
          {children}
        </div>
      </div>
    </div>
  )
}
